import * as React from 'react'
import { LauncherMotion } from '../motion/idle'
import { createMoveRequest, MascotSpecialMoves } from '../motion/special-moves'
import type { MoveOptions, MoveRequest } from '../motion/special-moves'
import type { MascotMove } from '../motion/move-catalog'
import type { Expression } from '../model/poses'
import { readMascotShape, writeMascotShape } from '../model/shape-storage'
import type { MascotHandle } from './mascot-handle'

export function useMascotController({
  active,
  ref,
}: {
  active: boolean
  ref?: React.Ref<MascotHandle>
}) {
  const [wakeToken, setWakeToken] = React.useState(0)
  const [ready, setReady] = React.useState(false)
  const motion = React.useMemo(() => new LauncherMotion(), [])
  React.useEffect(() => {
    motion.setPaused(!active)
  }, [active, motion])
  const moves = React.useMemo(() => {
    const player = new MascotSpecialMoves()
    player.setShape(readMascotShape())
    return player
  }, [])
  const requestRef = React.useRef<MoveRequest | null>(null)
  const sequence = React.useRef(0)
  const heldFace = React.useRef<Expression | null>(null)
  const speakingTimer = React.useRef<number | null>(null)
  const generating = React.useRef(false)
  const onFailure = React.useCallback(() => setReady(true), [])
  const onSession = React.useCallback((next: boolean) => setReady(next), [])
  const wake = React.useCallback(() => {
    setWakeToken(token => token + 1)
  }, [])
  const clearSpeakingTimer = React.useCallback(() => {
    if (speakingTimer.current !== null) {
      window.clearTimeout(speakingTimer.current)
      speakingTimer.current = null
    }
  }, [])
  const play = React.useCallback((move: MascotMove, options?: MoveOptions) => {
    sequence.current += 1
    requestRef.current = createMoveRequest(sequence.current, move, options)
    setWakeToken(token => token + 1)
  }, [])
  const show = React.useCallback(
    (expression: Expression | null) => {
      heldFace.current = expression
      if (!moves.speaking) {
        moves.pinned = expression
        setWakeToken(token => token + 1)
      }
    },
    [moves],
  )
  const setGenerating = React.useCallback(
    (active: boolean) => {
      if (generating.current === active) {
        return
      }
      generating.current = active
      moves.generating = active
      if (active) {
        heldFace.current = 'delighted'
        moves.pinned = 'delighted'
        play('generating_spin', { loop: true })
        return
      }
      requestRef.current = null
      heldFace.current = null
      moves.pinned = null
      wake()
    },
    [moves, play, wake],
  )
  const handleClick = React.useCallback(() => {
    moves.cycleShape()
    writeMascotShape(moves.shape)
    wake()
  }, [moves, wake])
  const stopSpeakingAnimation = React.useCallback(() => {
    clearSpeakingTimer()
    moves.speaking = false
    moves.pinned = heldFace.current
    setWakeToken(token => token + 1)
  }, [clearSpeakingTimer, moves])
  const activateSpeakingAnimation = React.useCallback(() => {
    clearSpeakingTimer()
    moves.speaking = true
    const step = (open: boolean) => {
      moves.pinned = open ? 'speaking' : 'relaxed'
      wake()
      const gap = open ? 140 + Math.floor(Math.random() * 120) : 80 + Math.floor(Math.random() * 90)
      speakingTimer.current = window.setTimeout(() => step(!open), gap)
    }
    step(true)
  }, [clearSpeakingTimer, moves, wake])

  React.useEffect(() => clearSpeakingTimer, [clearSpeakingTimer])

  React.useImperativeHandle(
    ref,
    () => ({ play, show, setGenerating, activateSpeakingAnimation, stopSpeakingAnimation }),
    [play, show, setGenerating, activateSpeakingAnimation, stopSpeakingAnimation],
  )

  return {
    wakeToken,
    ready,
    motion,
    moves,
    requestRef,
    onFailure,
    onSession,
    play,
    handleClick,
  }
}
